import type { Batcher, EventQueue, RetryPolicy } from './analytics-sdk.types';
import {
  DEFAULT_BATCH_SIZE,
  DEFAULT_BATCH_TIMEOUT_MS,
} from './analytics-sdk.types';

export const createBatcher = (
  queue: EventQueue,
  retryPolicy: RetryPolicy,
  sizeThreshold = DEFAULT_BATCH_SIZE,
  timeoutMs = DEFAULT_BATCH_TIMEOUT_MS,
): Batcher => {
  let isFlushing = false;
  let isDestroyed = false;
  let intervalId: ReturnType<typeof setInterval> | null = null;

  const flush = async (): Promise<void> => {
    if (isDestroyed || isFlushing || queue.size() === 0) return;

    isFlushing = true;
    const batch = queue.getBatch(sizeThreshold);

    try {
      const unsent = await retryPolicy.sendWithRetry(batch);
      const sentCount = batch.length - unsent.length;
      if (sentCount > 0 && !isDestroyed) {
        queue.confirmSent(sentCount);
      }
    } catch {
      // All events remain in queue — nothing to confirm.
    } finally {
      isFlushing = false;
    }
  };

  // Best-effort: the async flush may not complete before the browser
  // terminates the page. Pending events are already persisted to
  // localStorage and will be recovered on the next session.
  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      queue.persistImmediately();
      flush();
    }
  };

  const handlePageHide = (): void => {
    queue.persistImmediately();
    flush();
  };

  intervalId = setInterval(flush, timeoutMs);

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', handlePageHide);

  return {
    notify: () => {
      if (isDestroyed || queue.size() < sizeThreshold) return;
      flush();
    },

    flush,

    destroy: () => {
      isDestroyed = true;
      // Persist synchronously so in-memory events survive a programmatic
      // re-init (mirrors the page-hide path). At-least-once trade-off:
      // events in-flight at destroy time are snapshotted here and present
      // in the new queue — they may be sent twice. The isDestroyed flag
      // prevents the completed flush from calling confirmSent on the old
      // queue and corrupting the new instance's localStorage state.
      queue.persistImmediately();
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    },
  };
};
